import { formatBillingIntervalDays, pluralize } from "../../../app/_helpers";
import {
  CustomerSupportOverview,
  CustomerSupportPortalAction,
  CustomerSupportPortalActionBreakdown,
  CustomerSupportTopIntent,
  DashboardSections,
  DashboardState,
  GroupedChartRow,
  InfoCards,
  PortalActionScope,
  PortalActionType,
  SessionFilters,
} from "../../../app/_models";
import {
  CardState,
  DoughnutChartData,
  DualCardData,
  InfoCardData,
  InlineTableData,
  PerfTableColumn,
  PortalActionRow,
  SummaryCell,
  SummaryCellType,
  ViewToggleOption,
} from "../data-visualization";

import { previewAiInsights } from "./overview.preview-data";

export type OverviewCardState<T> = CardState<T>;

// The bar-chart series flattened into table columns for the PDF.
export interface CancellationReportRow {
  name: string;
  requests: string;
  saved: string;
  cancelled: string;
}

/* Cards Data */
export const overviewInfoCardsData: InfoCards = [
  {
    title: "Self-Service Rate",
    value: "",
    hint:
      "The self-service rate is determined by dividing the total number of successful customer " +
      "support sessions by the overall number of sessions, expressed as a percentage.",
  },
  {
    title: "Sessions",
    value: "",
    hint: "The overall count of user inquiries.",
  }
];

export const refundAmountPrefix = "Refund Amount: ";

export interface OverviewDualCard {
  leftLbl: string;
  leftVal: string;
  rightLbl: string;
  rightVal: string;
  subtitle: string;
  subtitleLoading?: boolean;
  percentage?: number | null;
  is_negative?: boolean;
  hint?: string;
}

export const overviewDualCards: OverviewDualCard[] = [
  {
    leftLbl: "All Sessions",
    leftVal: "--",
    rightLbl: "Unique",
    rightVal: "--",
    subtitle: "--",
    hint: "Shows customer sessions that started during the selected period, including return visits, and how many were unique (the Sessions table's default view).",
  },
  {
    leftLbl: "Order Cancels",
    leftVal: "--",
    rightLbl: "Saved",
    rightVal: "--",
    subtitle: refundAmountPrefix + "--",
    subtitleLoading: true,
    hint: "Shows the number of orders cancelled during the selected period and how many cancellation requests were saved.",
  },
  {
    leftLbl: "Sub Cancels",
    leftVal: "--",
    rightLbl: "Saved",
    rightVal: "--",
    subtitle: refundAmountPrefix + "--",
    subtitleLoading: true,
    hint: "Shows the number of subscriptions cancelled during the selected period and how many cancellation requests were saved.",
  },
];

export const overviewTopSingleCards: InfoCards = [
  {
    title: "Revenue Saved",
    value: "--",
    subtitle: "Revenue recovered across all solutions",
    hint: "Shows the total revenue retained during the selected period when customers requested refunds, often through discounts.",
  },
];

export enum OverviewDualCardIndex {
  AllSessions = 0,
  OrderCanceled = 1,
  SubscrCanceled = 2,
}

export enum SupportSummaryIndex {
  Phone = 0,
  Email = 1,
  DirectLinks = 2,
  Social = 3,
}

export const supportSummaryInfoCards: InfoCardData[] = [
  {
    title: "Phone",
    value: "",
    icon: "/assets/IconsLibrary/support-phone-icon.svg",
    hint: "Shows the total number of customer support interactions handled through phone during the selected period.",
  },
  {
    title: "Email",
    value: "",
    icon: "/assets/IconsLibrary/support-email-icon.svg",
    hint: "Shows the total number of customer support interactions handled through email during the selected period.",
  },
  {
    title: "Direct Links",
    value: "",
    icon: "/assets/IconsLibrary/support-direct-links-icon.svg",
    hint: "Shows the total number of support sessions started through direct links to your support experience.",
  },
  {
    title: "Social",
    value: "",
    icon: "/assets/IconsLibrary/support-social-icon.svg",
    hint: "Shows the total number of customer support interactions started through social channels.",
  },
];

export const supportSummaryCells: SummaryCell[] = supportSummaryInfoCards.map((card) => ({ type: SummaryCellType.Single, card }));

export const retentionSummaryCells: SummaryCell[] = [
  { type: SummaryCellType.Dual, card: overviewDualCards[OverviewDualCardIndex.AllSessions] },
  { type: SummaryCellType.Dual, card: overviewDualCards[OverviewDualCardIndex.OrderCanceled] },
  { type: SummaryCellType.Dual, card: overviewDualCards[OverviewDualCardIndex.SubscrCanceled] },
  { type: SummaryCellType.Single, card: overviewTopSingleCards[0] },
];

export const retentionSummaryReportOptions: ViewToggleOption[] = [
  { label: "Order Retention Report", value: DashboardSections.Order, icon: "cart" },
  { label: "Subscription Retention Report", value: DashboardSections.Subscription, icon: "refresh" },
];

export const retentionSummaryDashboardOptions: ViewToggleOption[] = [
  { label: "Order Retention Dashboard", value: DashboardSections.Order, icon: "cart" },
  { label: "Subscription Retention Dashboard", value: DashboardSections.Subscription, icon: "refresh" },
];

export enum MarketplaceSummaryIndex {
  Revenue = 0,
  AOV = 1,
  CompletedOrders = 2,
  ConversionRate = 3,
}

export const marketplaceSummaryInfoCards: InfoCardData[] = [
  {
    title: "Revenue",
    value: "",
    hint: "Shows the total revenue generated from completed marketplace orders during the selected period.",
  },
  {
    title: "AOV",
    value: "",
    hint: "Shows the average order value of completed marketplace orders during the selected period.",
  },
  {
    title: "Completed Orders",
    value: "",
    hint: "Shows the total number of marketplace orders successfully completed during the selected period.",
  },
  {
    title: "Conversion Rate",
    value: "",
    hint: "Shows the percentage of marketplace sessions that resulted in a completed order.",
  },
];

export const marketplaceSummaryCells: SummaryCell[] = marketplaceSummaryInfoCards.map((card) => ({
  type: SummaryCellType.Single,
  card,
}));

export enum ReturnSummaryIndex {
  ReturnRequests = 0,
  Deflected = 1,
}

export const returnSummaryInfoCards: InfoCardData[] = [
  {
    title: "Return Requests",
    value: "",
    hint: "Shows the total number of return requests submitted by your customers during the selected period.",
  },
  {
    title: "Deflected",
    value: "",
    hint: "Shows the number of return requests you prevented by offering alternative resolution options.",
  },
];

export enum ReturnSummaryDualIndex {
  ReturnsCompleted = 0,
  RefundAmountRate = 1,
}

export const returnSummaryDualCards: DualCardData[] = [
  {
    leftLbl: "Pending",
    rightLbl: "Completed",
    leftVal: "",
    rightVal: "",
    subtitle: "",
    hint: "Pending shows authorized returns still waiting for the item within the return window. Completed shows return requests that were authorized.",
  },
  {
    leftLbl: "Refund Amount",
    rightLbl: "Refund Rate",
    leftVal: "",
    rightVal: "",
    subtitle: "",
    hint: "Shows the percentage of return-related transactions that resulted in a refund.",
  },
];

export const returnSummaryCells: SummaryCell[] = [
  { type: SummaryCellType.Single, card: returnSummaryInfoCards[ReturnSummaryIndex.ReturnRequests] },
  { type: SummaryCellType.Single, card: returnSummaryInfoCards[ReturnSummaryIndex.Deflected] },
  { type: SummaryCellType.Dual, card: returnSummaryDualCards[ReturnSummaryDualIndex.ReturnsCompleted] },
  { type: SummaryCellType.Dual, card: returnSummaryDualCards[ReturnSummaryDualIndex.RefundAmountRate] },
];

export interface OverviewSummaryActivity {
  retention: boolean;
  marketplace: boolean;
  returns: boolean;
}

export const overviewSummaryHasActivity: OverviewSummaryActivity = {
  retention: false,
  marketplace: false,
  returns: false,
};

export function resetOverviewSummaryActivity(): void {
  overviewSummaryHasActivity.retention = false;
  overviewSummaryHasActivity.marketplace = false;
  overviewSummaryHasActivity.returns = false;
}

/* Charts Data */
export const sessionsDoughnutData: DoughnutChartData = {
  data: [100, 50, 60, 20, 10],
  noData: false,
  labels: ["Successful", "Bypass", "Dropoffs", "Lifeline"],
  loading: true,
  backgroundColors: [
    {
      backgroundColor: ["#163549", "#22B8D7", "#FFCC3E", "#B1BEC4", "#15C771"],
    },
  ],
};

export const sessionsTableData: InlineTableData = [
  {
    name: "Successful",
    color: "#163549",
  },
  {
    name: "Bypass",
    color: "#22B8D7",
  },
  {
    name: "Dropoffs",
    color: "#FFCC3E",
  },
  {
    name: "Lifeline",
    color: "#B1BEC4",
  },
];

const topIntentsColors = ["#00CC80", "#A6D8E8", "#FFD666", "#FF8971", "#798EFF"];
const TOP_INTENTS_LIMIT = 5;

export const topIntentsDoughnutData: DoughnutChartData = {
  data: [],
  noData: false,
  labels: [],
  loading: true,
  total: "0",
  backgroundColors: [{ backgroundColor: topIntentsColors }],
};

export const topIntentsTableData: InlineTableData = [];

export function applyTopIntents(
  intents: CustomerSupportTopIntent[],
  overview: CustomerSupportOverview
): void {
  const ranked = intents ? intents.slice(0, TOP_INTENTS_LIMIT) : [];
  const startedSessions = overview && overview.started_sessions ? overview.started_sessions : 0;

  topIntentsDoughnutData.data = ranked.map((intent: CustomerSupportTopIntent) => intent.session_count);
  topIntentsDoughnutData.labels = ranked.map((intent: CustomerSupportTopIntent) => intent.intent_name);
  topIntentsDoughnutData.backgroundColors = [
    { backgroundColor: topIntentsColors.slice(0, ranked.length) },
  ];
  topIntentsDoughnutData.total = startedSessions.toLocaleString();
  topIntentsDoughnutData.loading = false;
  topIntentsDoughnutData.noData = ranked.length === 0;

  topIntentsTableData.length = 0;

  ranked.forEach((intent: CustomerSupportTopIntent, index: number) => {
    topIntentsTableData.push({
      name: intent.intent_name,
      color: topIntentsColors[index],
      value: intent.session_count.toLocaleString(),
      percentage: intent.share.toFixed(1) + "%",
    });
  });
}

// Order and Subscription reasons stay separate cards, per design.
export const topOrderCancellationReasons: OverviewCardState<GroupedChartRow> = {
  data: [],
  loading: true,
  noData: false,
};

export const topSubscriptionCancellationReasons: OverviewCardState<GroupedChartRow> = {
  data: [],
  loading: true,
  noData: false,
};

export const topReturnReasons: OverviewCardState<GroupedChartRow> = {
  data: [],
  loading: true,
  noData: false,
};

export interface PortalMomentsRow {
  name: string;
  sessions: string;
  sessionPct: string;
}

export const portalMoments: OverviewCardState<PortalMomentsRow> = {
  data: [],
  loading: true,
  noData: false,
};

export const portalMomentsNameColumn: PerfTableColumn = { label: "Moments", field: "name" };
export const portalMomentsValueColumns: PerfTableColumn[] = [
  { label: "Sessions", field: "sessions" },
  { label: "% of Sessions", field: "sessionPct" },
];

export const portalActionsViews: ViewToggleOption[] = [
  { label: "Subscription", value: PortalActionScope.Subscription },
  { label: "Orders", value: PortalActionScope.Order },
];

export interface PortalActionsState extends CardState<PortalActionRow> {
  subscriptionData: PortalActionRow[] | null;
  orderData: PortalActionRow[] | null;
  scope: PortalActionScope;
}

export const portalActions: PortalActionsState = {
  data: [],
  subscriptionData: null,
  orderData: null,
  loading: true,
  noData: false,
  scope: PortalActionScope.Subscription,
};

export function applyPortalActionsScope(state: PortalActionsState, scope: PortalActionScope): void {
  state.scope = scope;
  const scopedData = scope === PortalActionScope.Order ? state.orderData : state.subscriptionData;

  if (scopedData !== null) {
    state.data = scopedData;
    state.noData = scopedData.length === 0;
    state.loading = false;
  } else {
    state.loading = true;
  }
}

const portalActionBreakdownValueFormatters: { [key in PortalActionType]: (value: string) => string } = {
  [PortalActionType.DelayShipment]: (value: string) => {
    const days = Number(value);
    let result = "Unknown";
    if (days > 0) {
      result = pluralize(days, "Day");
    }
    return result;
  },
  [PortalActionType.ChangeFrequency]: formatBillingIntervalDays,
  [PortalActionType.ProductSwap]: (value: string) => value || "Unknown",
  [PortalActionType.UpdatePayment]: (value: string) => value || "Unknown",
};

function mapPortalActionRow(action: CustomerSupportPortalAction): PortalActionRow {
  const children: PortalActionRow[] = action.breakdown.map((entry: CustomerSupportPortalActionBreakdown) => ({
    action: portalActionBreakdownValueFormatters[action.action_type](entry.value),
    count: entry.action_count.toLocaleString(),
    pctActions: entry.action_share.toFixed(1) + "%",
    pctSessions: entry.session_share.toFixed(1) + "%",
  }));

  return {
    action: action.action_name,
    count: action.action_count.toLocaleString(),
    pctActions: action.action_share.toFixed(1) + "%",
    pctSessions: action.session_share.toFixed(1) + "%",
    children,
  };
}

export function mapPortalActionRows(actions: CustomerSupportPortalAction[]): PortalActionRow[] {
  return actions
    .filter((action: CustomerSupportPortalAction) => action.action_type in portalActionBreakdownValueFormatters)
    .map(mapPortalActionRow);
}

export interface ResolvePerformanceStat {
  label: string;
  value: string;
  subtitle?: string;
}

// Impressions/Clicks/Actions have no endpoint yet - fixed at "--".
// The duplicate "Refund Amount" label is unconfirmed with the designer/PM.
export const resolvePerformanceStats: ResolvePerformanceStat[] = [
  { label: "Impressions", value: "--" },
  { label: "Clicks", value: "--" },
  { label: "Actions", value: "--" },
  { label: "Refunds", value: "" },
  { label: "Refund Amount", value: "" },
  { label: "Refund Amount", value: "" },
];

export enum ResolvePerformanceStatIndex {
  Impressions = 0,
  Clicks = 1,
  Actions = 2,
  Refunds = 3,
  RefundAmount = 4,
  FullRefundAmount = 5,
}

const destinationLabels = ["Manage Subscription", "Track Orders", "Customer Support Inquiry", "Request Refund"];
// Colors from Figma node 7928:1122.
const destinationColors = ["#798EFF", "#2BCAB1", "#FFA4C3", "#DFB3FF"];

// No endpoint for this yet.
export const clicksByDestination: DoughnutChartData = {
  data: [],
  noData: true,
  labels: destinationLabels,
  loading: false,
  total: "0",
  backgroundColors: [{ backgroundColor: destinationColors }],
};

export const clicksByDestinationLegend: InlineTableData = [
  { name: "Manage Subscription", color: destinationColors[0] },
  { name: "Track Orders", color: destinationColors[1] },
  { name: "Customer Support Inquiry", color: destinationColors[2] },
  { name: "Request Refund", color: destinationColors[3] },
];

export const clicksByDestinationHeaders: string[] = ["Destinations", "Clicks", "CTR"];

export const actionsByDestination: DoughnutChartData = {
  data: [],
  noData: true,
  labels: destinationLabels,
  loading: false,
  total: "0",
  backgroundColors: [{ backgroundColor: destinationColors }],
};

export const actionsByDestinationLegend: InlineTableData = [
  { name: "Manage Subscription", color: destinationColors[0] },
  { name: "Track Orders", color: destinationColors[1] },
  { name: "Customer Support Inquiry", color: destinationColors[2] },
  { name: "Request Refund", color: destinationColors[3] },
];

export const actionsByDestinationHeaders: string[] = ["Destinations", "Clicks", "Action %"];

export interface OverviewAiInsight {
  text: string;
}

// No AI-insights generation exists yet - needs backend/AI work.
export const overviewAiInsights: OverviewCardState<OverviewAiInsight> = {
  data: previewAiInsights,
  loading: false,
  noData: false,
};

const sessionFilters: SessionFilters = {
  includeTestSessions: false,
  includeHangups: false,
  includeUnknowns: false,
};

const state: DashboardState = {};

export const sessionFinalizationHours = 12;

export const sessions = {
  filter: null,
  flag: false,
  filters: sessionFilters,
  display: false,
  state,
};

export interface TopFaqRow {
  name: string;
  sessions: string;
  pctSupport: string;
}

export const faqs: OverviewCardState<TopFaqRow> = {
  data: [],
  loading: true,
  noData: false,
};

export const topFaqsNameColumn: PerfTableColumn = { label: "Question", field: "name" };
export const topFaqsValueColumns: PerfTableColumn[] = [
  { label: "Sessions", field: "sessions" },
  { label: "% of Support", field: "pctSupport" },
];

export const campaigns = {
  data: [],
  loading: true,
  title: "Top Campaigns",
};

export enum PathChannelSource {
  Web = 0,
  Email = 1,
  Phone = 2,
  SocialMedia = 3,
}

export enum OverviewInfo {
  SelfService = 0,
  Sessions = 1,
}

export const overviewHints = {
  sessions: {
    success: `Customer sessions completed successfully, meeting their objectives.`,
    bypass: `Customers who opted to be directed to a live customer support agent.`,
    dropoff: `Customers who abandoned their support session.`,
    lifeline: `Customers offered website popup help through phone, email or chat due to friction score or errors for quick resolutions.`,
    unmatch: `Customers not found or identified in their ecommerce platform.`,
  },
  inquiry: "The type of requests customers are submitting.",
  faq: "The most frequently asked questions that your customers are inquiring about.",
  campaign:
    "Most active ecommerce campaigns submitting customer support inquiries.",
  sessionsTable: "Real-time session details",
  steps: "The count of unique steps taken by a customer during their session.",
  frequency: "The number of times a customer initiated a session.",
  refundsReturns: "How return requests were handled and their impact on refunded revenue.",
  topIntents: "The most common reasons customers started a session, ranked by session count.",
  portalMoments: "How sessions break down across key moments in the customer portal.",
  portalActions: "The self-service actions customers completed, and how they were retained.",
  resolvePerformance: "How support-resolution touchpoints performed, and their refund impact.",
  clicksByDestination: "Where customers clicked to during their resolution journey.",
  actionsByDestination: "Where customers completed an action during their resolution journey.",
  aiInsights: "AI-generated highlights surfaced from your account's recent activity.",
};
